/**
 * Which pair of policy versions the comparison page is showing.
 *
 * Pure, and in its own module rather than inside ComparisonPage, so it can be tested
 * without pulling in ReactMarkdown and @pierre/diffs — both ESM, which jest cannot parse
 * without mocking.
 *
 * Two rules here exist because their absence was a bug:
 *
 * 1. **The opening pair is pinned once per criterion.** It is the first *unjudged* version,
 *    so recomputing it after a decision jumped the page to the next pair the moment you
 *    judged one.
 * 2. **Selection is held as IDs, resolved against the latest fetch.** Holding version
 *    objects meant the verdict buttons kept showing the status from before the decision was
 *    saved.
 */
import { PolicyVersionState } from '../../../types';

const byVersion = (versions: PolicyVersionState[]) =>
  [...versions].sort((a, b) => a.version - b.version);

const currentOf = (versions: PolicyVersionState[]): PolicyVersionState | undefined => {
  const ordered = byVersion(versions);
  return ordered.find(version => version.isCurrent) ?? ordered[ordered.length - 1];
};

/**
 * The pair to open a criterion on: its first unjudged version against the current one, or
 * failing that any non-current version.
 */
export const defaultPairIds = (versions: PolicyVersionState[]): [string, string] | null => {
  const ordered = byVersion(versions);
  const current = currentOf(versions);
  if (!current) return null;
  const older =
    ordered.find(version => version.status === 'unjudged') ??
    ordered.find(version => version.policyId !== current.policyId);
  return older ? [older.policyId, current.policyId] : null;
};

/**
 * Resolve selected policy IDs against the latest `versions`, older first.
 *
 * Returns null if either ID has gone — a criterion switched underneath, say — so the caller
 * falls back to the default rather than rendering a half-pair.
 */
export const resolvePair = (
  versions: PolicyVersionState[],
  selectedIds: [string, string] | null
): [PolicyVersionState, PolicyVersionState] | null => {
  const ids = selectedIds ?? defaultPairIds(versions);
  if (!ids || ids[0] === ids[1]) return null;

  const lookup = new Map(versions.map(version => [version.policyId, version]));
  const older = lookup.get(ids[0]);
  const newer = lookup.get(ids[1]);
  if (!older || !newer) return null;

  return older.version <= newer.version ? [older, newer] : [newer, older];
};
